import MatchFullModel from "../../../Football/Models/Match/MatchFullModel";
import TimelineModel from "../../../Football/Models/Match/Timeline/TimelineModel";
import PlayerBasicModel from "../../../Football/Models/Player/PlayerShortModel";
import {
  DoubleChanceEnum,
  HalfTimeFullTimeEnum,
  OneXTwoEnum,
} from "../../Enums/ValueEnums";
import MatchMarketsResultsModel from "../MatchMarketsResults/MatchMarketsResultsModel";
import MatchPlayerMarketResultModel from "../MatchMarketsResults/MatchPlayerMarketResultModel";

export default class MatchMarketResultsFactory {
  private constructor() { }

  public static createMatchMarketsResultsModel(
    match: MatchFullModel, playersMap: Map<string, PlayerBasicModel>): MatchMarketsResultsModel {
    return {
      FT_1X2: this.createOneXTwoResult(match.scores, "full_time"),
      HT_1X2: this.createOneXTwoResult(match.scores, "half_time"),
      BOTH_TEAMS_SCORE:
        match.scores.ftScore.homeGoals > 0 &&
        match.scores.ftScore.awayGoals > 0,
      DOUBLE_CHANCE: this.createDoubleChanceResult(match.scores),
      HT_FT: this.createHalfTimeFullTimeResult(match.scores),
      RED_CARD_MATCH: this.createMarketResultByCriteria(match, "red_card"),
      PENALTY_MATCH: this.createMarketResultByCriteria(match, "penalty"),
      CORNERS_MATCH: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam,
      PLAYER_YELLOW_CARD: this.createPlayersMarketsResults(
        match,
        playersMap,
        "yellow_card"
      ) as MatchPlayerMarketResultModel[],
      PLAYER_RED_CARD: this.createPlayersMarketsResults(
        match,
        playersMap,
        "red_card"
      ) as MatchPlayerMarketResultModel[],
      PLAYER_SCORE_FIRST_GOAL: this.createPlayersMarketsResults(
        match,
        playersMap,
        "first_goal"
      ) as MatchPlayerMarketResultModel,
      PLAYER_SCORE: this.createPlayersMarketsResults(
        match,
        playersMap,
        "one_goal"
      ) as MatchPlayerMarketResultModel[],
      PLAYER_SCORE_TWICE: this.createPlayersMarketsResults(
        match,
        playersMap,
        "two_goals"
      ) as MatchPlayerMarketResultModel[],
      PLAYER_SCORE_HATTRICK: this.createPlayersMarketsResults(
        match,
        playersMap,
        "hattrick"
      ) as MatchPlayerMarketResultModel[],
      CORRECT_SCORE: {
        homeGoals: match.scores.ftScore.homeGoals,
        awayGoals: match.scores.ftScore.awayGoals,
      },
      CORRECT_SCORE_ADVANCED: {
        homeGoals: match.scores.ftScore.homeGoals,
        awayGoals: match.scores.ftScore.awayGoals,
      },
      CORRECT_SCORE_HT: {
        homeGoals: match.scores.htScore.homeGoals,
        awayGoals: match.scores.htScore.awayGoals,
      },
      OVER_GOALS_0_5:
        match.scores.ftScore.homeGoals + match.scores.ftScore.awayGoals > 0,
      OVER_GOALS_1_5:
        match.scores.ftScore.homeGoals + match.scores.ftScore.awayGoals > 1,
      OVER_GOALS_2_5:
        match.scores.ftScore.homeGoals + match.scores.ftScore.awayGoals > 2,
      OVER_GOALS_3_5:
        match.scores.ftScore.homeGoals + match.scores.ftScore.awayGoals > 3,
      OVER_GOALS_4_5:
        match.scores.ftScore.homeGoals + match.scores.ftScore.awayGoals > 4,
      OVER_GOALS_5_5:
        match.scores.ftScore.homeGoals + match.scores.ftScore.awayGoals > 5,
      OVER_GOALS_6_5:
        match.scores.ftScore.homeGoals + match.scores.ftScore.awayGoals > 6,
      OVER_CORNERS_6_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 6,
      OVER_CORNERS_7_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 7,
      OVER_CORNERS_8_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 8,
      OVER_CORNERS_9_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 9,
      OVER_CORNERS_10_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 10,
      OVER_CORNERS_11_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 11,
      OVER_CORNERS_12_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 12,
      OVER_CORNERS_13_5: isNaN(
        match.stats?.corners?.homeTeam + match.stats?.corners?.awayTeam
      )
        ? null
        : match.stats.corners.homeTeam + match.stats.corners.awayTeam > 13,
    };
  }

  private static createOneXTwoResult = (
    scores: any,
    period: "half_time" | "full_time"
  ) => {
    if (period === "full_time") {
      if (scores.ftScore.homeGoals > scores.ftScore.awayGoals) {
        return OneXTwoEnum.ONE;
      }

      if (scores.ftScore.homeGoals === scores.ftScore.awayGoals) {
        return OneXTwoEnum.X;
      }

      return OneXTwoEnum.TWO;
    }

    if (scores?.htScore?.homeGoals > scores?.htScore?.awayGoals) {
      return OneXTwoEnum.ONE;
    }

    if (scores?.htScore?.homeGoals === scores?.htScore?.awayGoals) {
      return OneXTwoEnum.X;
    }

    return OneXTwoEnum.TWO;
  };

  private static createDoubleChanceResult = (scores: any) => {
    if (scores?.ftScore?.homeGoals > scores?.ftScore?.awayGoals) {
      return [DoubleChanceEnum.ONE_X, DoubleChanceEnum.ONE_TWO];
    }

    if (scores?.ftScore?.homeGoals === scores?.ftScore?.awayGoals) {
      return [DoubleChanceEnum.ONE_X, DoubleChanceEnum.X_TWO];
    }

    return [DoubleChanceEnum.X_TWO, DoubleChanceEnum.ONE_TWO];
  };

  private static createHalfTimeFullTimeResult = (scores: any) => {
    if (scores?.htScore?.homeGoals > scores?.htScore?.awayGoals) {
      if (scores?.ftScore?.homeGoals > scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.ONE_ONE;
      }

      if (scores?.ftScore?.homeGoals === scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.ONE_X;
      }

      if (scores?.ftScore?.homeGoals < scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.ONE_TWO;
      }
    }

    if (scores?.htScore?.homeGoals === scores?.htScore?.awayGoals) {
      if (scores?.ftScore?.homeGoals > scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.X_ONE;
      }

      if (scores?.ftScore?.homeGoals === scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.X_X;
      }

      if (scores?.ftScore?.homeGoals < scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.X_TWO;
      }
    }

    if (scores?.htScore?.homeGoals < scores?.htScore?.awayGoals) {
      if (scores?.ftScore?.homeGoals > scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.TWO_ONE;
      }

      if (scores?.ftScore?.homeGoals === scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.TWO_X;
      }

      if (scores?.ftScore?.homeGoals < scores?.ftScore?.awayGoals) {
        return HalfTimeFullTimeEnum.TWO_TWO;
      }
    }
  };

  private static createMarketResultByCriteria = (
    match: MatchFullModel,
    criteria: "red_card" | "penalty"
  ) => {
    if (!match.timeline || !match.timeline.length) {
      return null;
    }

    // Red card can be checked in match.stats.redCards1h and match.stats.redCards2h
    if (criteria === "red_card") {
      return match.timeline
        .filter((e: TimelineModel) => Number(e.minute) <= 90)
        .some((event) => event.type === "red_card"); 
    }

    // For event type in timeline we have penalty_missed and penalty_scored.
    return match.timeline
      .filter((e: TimelineModel) => Number(e.minute) <= 90)
      .some((event) => event.type.startsWith(criteria));
  };

  private static createPlayersMarketsResults = (
    match: MatchFullModel,
    playersMap: Map<string, PlayerBasicModel>,
    criteria:
      | "first_goal"
      | "yellow_card"
      | "red_card"
      | "one_goal"
      | "two_goals"
      | "hattrick"
  ) => {
    if (!match.timeline || !match.timeline.length) {
      if (criteria === "first_goal") {
        return null;
      }

      return [];
    }

    if (criteria === "first_goal") {
      const firstGoal =
        match.timeline.find(
          (e: TimelineModel) =>
            e.type === "goal" ||
            e.type === "penalty_goal" ||
            e.type === "own_goal"
        ) || null;

      if (firstGoal) {
        const playerBasic = playersMap[firstGoal.player.id];
        return {
          playerId:
            firstGoal.type === "goal" || firstGoal.type === "penalty_goal"
              ? firstGoal.player.id
              : "OWN_GOAL",
          playerModel: playerBasic,
        };
      }

      return null;
    }

    if (criteria === "yellow_card" || criteria === "red_card") {
      return this.transformPlayerMarketResult(
        match.timeline
          .filter((e: TimelineModel) => Number(e.minute) <= 90)
          .filter((e: TimelineModel) => e.type === criteria)
          .map((e: TimelineModel) => playersMap[e.player.id])
      );
    }

    // The map will contain the player id as a key and array of player basic model objects.
    // For example when we have a player with three goals we will have the same player short model object three times
    const playersGoalsMap = new Map();
    const goalsLength =
      criteria === "one_goal" ? 1 : criteria === "two_goals" ? 2 : 3;
    const players = [];
    match.timeline
      .filter((e: TimelineModel) => Number(e.minute) <= 90)
      .filter((e: TimelineModel) => e.type === "goal" || e.type === "penalty_goal"
      )
      .forEach((e: TimelineModel) => {
        const playerBasic = playersMap[e.player.id];
        if (playersGoalsMap.has(e.player.id)) {
          const players = playersGoalsMap.get(e.player.id);
          players.push(playerBasic);
          playersGoalsMap.set(e.player.id, players);
        } else {
          playersGoalsMap.set(e.player.id, [playerBasic]);
        }
      });
    playersGoalsMap.forEach((p) => {
      if (p.length >= goalsLength) {
        players.push(p[0]);
      }
    });

    return this.transformPlayerMarketResult(players);
  };

  private static transformPlayerMarketResult = (
    players: PlayerBasicModel[]
  ) => {
    return players.map((player) => {
      return {
        playerId: player.id,
        playerModel: player,
      };
    });
  };
}
